import { actions, dom } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp, { AppComponent, Application, ParsedComponentId, GridEditor, EditorContainer } from '@reporting/rp-app-host';
import { Sidebar } from './components';
import { TablesGridAdapter, DatabaseTreeAdapter, ServerTreeAdapter, ErrorLogGridAdapter, JobActivityMonitorGridAdapter,
	DatabaseGridAdapter, ReportStatsGridAdapter, JobScheduleGridAdapter, DomainGridEditorAdapter, SsoPresetGridEditorAdapter, databaseKey,
	HistoryTableTreeAdapter, HistoryGridAdapter, historyTableLabel } from './adapters';
import { AccountingGroupTreeAdapter } from '@reporting/rp-mf-accounting';
import { WEEK_DAYS } from './types';
import { ErrorEntity } from '@reporting/rp-types/core';
import { SuperApplication } from '.';

class WeekDayAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private options?: Partial<TreeOptions>,
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
        this.items = WEEK_DAYS.map(d => ({
			k: d.day.toString(),
			v: msg(d.name),
		}));
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}
}

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	const sidebar = dom.createElement<Sidebar>('rp-super-sidebar');
	return sidebar;
}

const createTablesGrid = async (app: Application, componentId: ParsedComponentId) => {
	// super-tables-<id_server>[-<db_name>]
	const idServer = parseInt(componentId.ids[1]);
	const dbName = componentId.ids[2] || null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new TablesGridAdapter(idServer, dbName);
	return component;
}

const createTablesGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	const currServer = await (app as SuperApplication).getCurrentServer();
	// the current server's super database is selected at the start (the server entity carries its name)
	const defaultKey = databaseKey(currServer.id_server, currServer.db_name);
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'server',
			width: 300,
			type: 'dropdown',
			value: defaultKey,
			caption: `${msg('Server')} / ${msg('Database')}`,
			adapter: new DatabaseTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change, the key is `<id_server>-<db_name>`
		updateComponentId: (values) => {
			const key = values['server'];
			return `super-tables-${key || defaultKey}`;
		}
	});

	return container;
}

const createErrorLogGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ErrorLogGridAdapter();
	component.addEventListener('editor.action', (e: actions.ActionEvent) => {
		rp.host.open(`super-errorDetail-${e.action.value}`, {title: `${msg('Error')} #${e.action.value}`});
	});
	return component;
}

const createErrorDetailComponent = async (app: Application, componentId: ParsedComponentId) => {
	const idz = parseInt(componentId.ids[1]);
	const error = await rp.ms('server').get<ErrorEntity>(`/errors/${idz}`);

	// if the content looks like a full HTML document, render it in an iframe
	if (error?.content && (/^\s*<!doctype\s+/i.test(error.content) || /<html[\s>]/i.test(error.content))) {
		const iframe = dom.createElement<HTMLIFrameElement>('iframe');
		iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
		iframe.style.width = '100%';
		iframe.style.height = '100%';
		iframe.srcdoc = error.content;
		return iframe as unknown as AppComponent;
	}
	// otherwise, render it in a div
	const div = dom.createElement<HTMLDivElement>('div');
	div.innerHTML = error?.content || msg('No content available');
	return div as unknown as AppComponent;
}

const createJobActivityMonitorGrid = async (app: Application, componentId: ParsedComponentId) => {
	const idServer = parseInt(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new JobActivityMonitorGridAdapter(idServer);
	return component;
}

const createJobActivityMonitorGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	const currServer = await (app as SuperApplication).getCurrentServer();
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'server',
			width: 300,
			type: 'dropdown',
			value: currServer.host,
			caption: msg('Server'),
			adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const key = values['server'];
			return `super-jobActivityMonitor-${key || currServer.id_server}`;
		}
	});

	return container;
}

const createJobScheduleGrid = async (app: Application, componentId: ParsedComponentId) => {
	const idServer = parseInt(componentId.ids[1]);
	const weekDay = componentId.ids[2] ? parseInt(componentId.ids[2]) : 1;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new JobScheduleGridAdapter(idServer, weekDay);
	return component;
}

const createJobScheduleGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	const currServer = await (app as SuperApplication).getCurrentServer();
	const currWeekDay = new Date().getDay() || 1;
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'server',
			width: 300,
			type: 'dropdown',
			value: currServer.host,
			caption: msg('Server'),
			adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}, {
			id: 'weekDay',
			width: 200,
			type: 'dropdown',
			value: msg(WEEK_DAYS.find(d => d.day === currWeekDay)?.name),
			caption: msg('Week Day'),
			adapter: new WeekDayAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const server = values['server'];
			const weekDay = values['weekDay'];
			return `super-jobSchedule-${server || currServer.id_server}-${weekDay || currWeekDay}`;
		}
	});

	return container;
}

const createDatabaseGrid = async (app: Application, componentId: ParsedComponentId) => {
	const idServer = parseInt(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DatabaseGridAdapter(idServer);
	return component;
}

const createDatabaseGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	const currServer = await (app as SuperApplication).getCurrentServer();
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'server',
			width: 300,
			type: 'dropdown',
			value: currServer.host,
			caption: msg('Server'),
			adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const key = values['server'];
			return `super-database-${key || currServer.id_server}`;
		}
	});

	return container;
}

const createDomainsGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DomainGridEditorAdapter();
	return component;
}

const createSsoPresetsGrid = async (app: Application, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SsoPresetGridEditorAdapter();
	return component;
}

const createReportsGrid = async (app: Application, componentId: ParsedComponentId) => {
	const idServer = parseInt(componentId.ids[1]);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new ReportStatsGridAdapter(idServer);
	return component;
}

const createReportsGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	const currServer = await (app as SuperApplication).getCurrentServer();
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'server',
			width: 300,
			type: 'dropdown',
			value: currServer.host,
			caption: msg('Server'),
			adapter: new ServerTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const key = values['server'];
			return `super-reportStats-${key || currServer.id_server}`;
		}
	});

	return container;
}

const createHistoryGrid = async (app: Application, componentId: ParsedComponentId) => {
	// super-history-<accounting>-<table>, a negative accounting doubles the separator (super-history--1-…)
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new HistoryGridAdapter(parseInt(componentId.ids[1]), componentId.ids[2]);
	return component;
}

const createHistoryGridContainer = async (app: Application, componentId: ParsedComponentId) => {
	// the current company is selected at the start; the history is read on its server, master tables from its
	// primary master database
	const accounting = rp.accountingProvider.accounting;
	const defaultTable = { table: 'D_STATEMENT', name: 'Statements' };
	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'accounting',
			width: 300,
			type: 'dropdown',
			value: [[`a-${accounting.accounting}`, 0, 1, { v: `${accounting.accounting} ${accounting.company}` }]],
			caption: msg('Company'),
			// the groups are headings only, a company has to be picked
			adapter: new AccountingGroupTreeAdapter({}, { selectMode: 'single', autoClose: true, checkboxes: false, search: true, selectableGroups: false }),
		}, {
			id: 'table',
			width: 340,
			type: 'dropdown',
			value: [[defaultTable.table, 0, 1, { v: historyTableLabel(defaultTable) }]],
			caption: msg('Table'),
			adapter: new HistoryTableTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false, search: true }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change, the company key is `a-<accounting>`
		updateComponentId: (values) => {
			const a = (values['accounting'] as string)?.slice(2) || accounting.accounting;
			return `super-history-${a}-${values['table'] || defaultTable.table}`;
		},
	});

	return container;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['super', createMainSidebar],
	['tables', createTablesGrid],
	['tablesContainer', createTablesGridContainer],
	['errorLog', createErrorLogGrid],
	['errorDetail', createErrorDetailComponent],
	['jobActivityMonitor', createJobActivityMonitorGrid],
	['jobActivityContainer', createJobActivityMonitorGridContainer],
	['jobSchedule', createJobScheduleGrid],
	['jobScheduleContainer', createJobScheduleGridContainer],
	['database', createDatabaseGrid],
	['databaseContainer', createDatabaseGridContainer],
	['domains', createDomainsGrid],
	['ssoPresets', createSsoPresetsGrid],
	['reportStats', createReportsGrid],
	['reportStatsContainer', createReportsGridContainer],
	['history', createHistoryGrid],
	['historyContainer', createHistoryGridContainer],
]);
